import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { getData } from "../../../FetchApiServices";
import { getLookupByType } from "../../../api/t2tVendorLookupApi";

// ---- Account Groups ----
const ACCOUNT_GROUPS = [
  { code: "Z101", label: "Domestic \u2014 Regular", type: "domestic" },
  { code: "Z102", label: "Domestic \u2014 Simplified", type: "domestic" },
  { code: "Z103", label: "Domestic \u2014 Seasonal/Kaccha", type: "domestic" },
  { code: "Z104", label: "Domestic \u2014 Service Provider", type: "domestic" },
  { code: "Z105", label: "Domestic \u2014 One-Time", type: "domestic" },
  { code: "Z201", label: "International \u2014 Regular", type: "international" },
  { code: "Z202", label: "International \u2014 Simplified", type: "international" },
];

// ---- Indian States with SAP codes ----
const INDIAN_STATES = [
  { code: "AN", name: "Andaman and Nicobar" }, { code: "AP", name: "Andhra Pradesh" }, { code: "AR", name: "Arunachal Pradesh" },
  { code: "AS", name: "Assam" }, { code: "BR", name: "Bihar" }, { code: "CG", name: "Chhattisgarh" },
  { code: "CH", name: "Chandigarh" }, { code: "DD", name: "Daman and Diu" }, { code: "DL", name: "Delhi" },
  { code: "GA", name: "Goa" }, { code: "GJ", name: "Gujarat" }, { code: "HP", name: "Himachal Pradesh" },
  { code: "HR", name: "Haryana" }, { code: "JH", name: "Jharkhand" }, { code: "JK", name: "Jammu and Kashmir" },
  { code: "KA", name: "Karnataka" }, { code: "KL", name: "Kerala" }, { code: "LA", name: "Ladakh" },
  { code: "MH", name: "Maharashtra" }, { code: "ML", name: "Meghalaya" }, { code: "MN", name: "Manipur" },
  { code: "MP", name: "Madhya Pradesh" }, { code: "MZ", name: "Mizoram" }, { code: "NL", name: "Nagaland" },
  { code: "OD", name: "Odisha" }, { code: "PB", name: "Punjab" }, { code: "PY", name: "Puducherry" },
  { code: "RJ", name: "Rajasthan" }, { code: "SK", name: "Sikkim" }, { code: "TN", name: "Tamil Nadu" },
  { code: "TS", name: "Telangana" }, { code: "TR", name: "Tripura" }, { code: "UK", name: "Uttarakhand" },
  { code: "UP", name: "Uttar Pradesh" }, { code: "WB", name: "West Bengal" },
];

const VENDOR_CATEGORIES = ["Manufacturer", "Trader", "Service Provider", "Contractor", "Labour", "Farmer/Aarthi", "Other"];
const GST_REG_TYPES = ["Regular", "Composition", "Unregistered"];
const PAYMENT_TERMS = [
  { code: "Z001", label: "30 days" }, { code: "Z002", label: "45 days" },
  { code: "Z003", label: "60 days" }, { code: "Z004", label: "90 days" },
  { code: "Z005", label: "Immediate" },
];
const BANK_ACCOUNT_TYPES = ["Savings", "Current", "Overdraft"];
const PARTNER_FUNCTIONS = [
  { id: "partner_function_vn", label: "Vendor (VN)", desc: "Every vendor is their own VN partner", required: true },
  { id: "partner_function_oa", label: "Ordering Address (OA)", desc: "If ordering address is different" },
  { id: "partner_function_gs", label: "Goods Supplier (GS)", desc: "If goods supplied by a different entity" },
  { id: "partner_function_pi", label: "Invoicing Party (PI)", desc: "If invoices come from a different vendor" },
  { id: "partner_function_az", label: "Payee (AZ)", desc: "If payment goes to a different entity" },
];
const DOCUMENT_TYPES = [
  { id: "doc_pan_card",        label: "PAN Card Copy",              requiredFor: ["Z101","Z102","Z103","Z104","Z105"], accept: ".pdf,.jpg,.png", maxMB: 5 },
  { id: "doc_gst_certificate", label: "GST Registration Certificate", requiredFor: ["Z101","Z102","Z104"], accept: ".pdf", maxMB: 5, hasExpiry: true },
  { id: "doc_trade_licence",   label: "Trade Licence / Shop Act",   requiredFor: [], accept: ".pdf", maxMB: 5, hasExpiry: true },
  { id: "doc_incorporation",   label: "Certificate of Incorporation", requiredFor: [], accept: ".pdf", maxMB: 5 },
  { id: "doc_msme_cert",       label: "MSME Certificate",           requiredFor: [], accept: ".pdf", maxMB: 5, hasExpiry: true, condition: "msme_registered" },
  { id: "doc_cancelled_cheque",label: "Cancelled Cheque",           requiredFor: ["Z101","Z102","Z103","Z104","Z105"], accept: ".pdf,.jpg", maxMB: 5 },
  { id: "doc_bank_statement",  label: "Bank Statement (3 months)",  requiredFor: [], accept: ".pdf", maxMB: 10 },
];

// ---- PAN validation regex ----
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]{3}$/;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const PIN_REGEX = /^[0-9]{6}$/;

/**
 * T2TVendorForm — Domestic vendor form with 6 sections.
 * Used inside T2TSubmit.js or standalone for vendor creation workflows.
 *
 * Props:
 *  - data: object (form values)
 *  - onChange: (field, value) => void
 *  - stage: string ("initiator"|"checker"|"finance"|"approver") — controls field visibility/editability
 *  - accountGroup: string (Z101-Z105) — drives which fields show
 *  - errors: object (field-level validation errors)
 *  - readOnly: bool
 */
const T2TVendorForm = ({ data = {}, onChange, stage = "initiator", accountGroup, errors = {}, readOnly = false }) => {
  const [activeSection, setActiveSection] = useState(1);
  const [orgUnits, setOrgUnits] = useState([]);
  const [liveCompanyCodes, setLiveCompanyCodes] = useState([]);
  const [livePaymentTerms, setLivePaymentTerms] = useState([]);
  const [livePlantCodes,   setLivePlantCodes]   = useState([]);

  const ag = accountGroup || data.account_group || "";
  const _isDomestic = ag.startsWith("Z1"); // eslint-disable-line no-unused-vars
  const isFastTrack = ag === "Z103" || ag === "Z105";
  const isFinanceStage = stage === "finance";
  const isCheckerOrApprover = stage === "checker" || stage === "approver";

  // Fetch org units for purchase_org picker
  useEffect(() => {
    getData("t2t/org-units").then((resp) => {
      if (!resp.error) {
        const raw = resp.data?.data;
        setOrgUnits(Array.isArray(raw) ? raw : []);
      }
    });
  }, []);

  // Fetch live lookup tables — fall back to static arrays if API fails
  useEffect(() => {
    getLookupByType("company_code").then((r) => { if (!r.error && r.data?.data?.length) setLiveCompanyCodes(r.data.data); });
    getLookupByType("payment_term").then((r)  => { if (!r.error && r.data?.data?.length) setLivePaymentTerms(r.data.data); });
    getLookupByType("plant_code").then((r)    => { if (!r.error && r.data?.data?.length) setLivePlantCodes(r.data.data); });
  }, []);

  // Merge live lookups with static fallbacks
  const companyCodes  = liveCompanyCodes.length  ? liveCompanyCodes  : ACCOUNT_GROUPS.filter((g) => g.type === "domestic");
  const paymentTerms  = livePaymentTerms.length  ? livePaymentTerms  : PAYMENT_TERMS;
  const plantCodes    = livePlantCodes.length     ? livePlantCodes    : [];

  const set = useCallback((field, value) => {
    if (readOnly || isCheckerOrApprover) return;
    if (onChange) onChange(field, value);
  }, [onChange, readOnly, isCheckerOrApprover]);

  // PAN auto-fill logic — withholding tax type
  useEffect(() => {
    if (!data.pan_number) return;
    if (PAN_REGEX.test(data.pan_number.toUpperCase())) {
      const panType = { C: "Company", P: "Individual", F: "Firm", T: "Trust", H: "HUF" }[data.pan_number[3]] || "Other";
      if (onChange) {
        onChange("pan_type", panType);
        onChange("withholding_tax_type", "4Q");
        onChange("section_code", "194Q");
      }
    } else if (data.pan_number.length === 0 && onChange) {
      onChange("withholding_tax_type", "5Q");
    }
  }, [data.pan_number]); // eslint-disable-line react-hooks/exhaustive-deps

  const fieldErr = (field) => errors[field] ? <div style={{ fontSize: 10, color: "#DC2626", marginTop: 2 }}>{errors[field]}</div> : null;

  const sections = [
    { id: 1, label: "General Info", icon: "📋" },
    { id: 2, label: "Address", icon: "📍" },
    { id: 3, label: "Tax & Compliance", icon: "🏛", financeOnly: true },
    { id: 4, label: "Banking", icon: "🏦", financeOnly: true },
    { id: 5, label: "Partner Functions", icon: "🔗" },
    { id: 6, label: "Documents", icon: "📎" },
  ];

  // Finance-only sections hidden from Initiator
  const visibleSections = sections.filter((s) => {
    if (s.financeOnly && stage === "initiator") return false;
    return true;
  });

  return (
    <div className="t2t-vendor-form">
      {/* Section tabs */}
      <div style={{ display: "flex", gap: 2, marginBottom: 20, flexWrap: "wrap" }}>
        {visibleSections.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setActiveSection(s.id)}
            style={{
              padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer",
              border: "1px solid", transition: "0.15s",
              background: activeSection === s.id ? "var(--t2t-primary)" : "white",
              color: activeSection === s.id ? "white" : "var(--t2t-text-secondary)",
              borderColor: activeSection === s.id ? "var(--t2t-primary)" : "#E2E8F0",
            }}
          >
            {s.icon} {s.label}
          </button>
        ))}
      </div>

      {/* ======== SECTION 1: Basic & General ======== */}
      {activeSection === 1 && (
        <div className="t2t-vendor-section">
          <div className="t2t-fg">
            <label>Account Group *</label>
            <select className="t2t-fc" value={data.account_group || ""} onChange={(e) => set("account_group", e.target.value)} disabled={readOnly || isCheckerOrApprover}>
              <option value="">Select account group...</option>
              {ACCOUNT_GROUPS.filter((g) => g.type === "domestic").map((g) => (
                <option key={g.code} value={g.code}>{g.code} \u2014 {g.label}</option>
              ))}
            </select>
            {fieldErr("account_group")}
          </div>

          <div className="t2t-fg">
            <label>Vendor / Company Name * <span style={{ fontWeight: 400, fontSize: 10, color: "var(--t2t-text-muted)" }}>(max 35 chars for SAP)</span></label>
            <input className="t2t-fc" value={data.vendor_name || ""} onChange={(e) => set("vendor_name", e.target.value.slice(0, 35))} placeholder="Legal name as registered" disabled={readOnly || isCheckerOrApprover} maxLength={35} />
            {data.vendor_name && <div style={{ fontSize: 10, color: data.vendor_name.length > 30 ? "#D97706" : "var(--t2t-text-muted)", textAlign: "right" }}>{data.vendor_name.length}/35</div>}
            {fieldErr("vendor_name")}
          </div>

          <div className="t2t-fg">
            <label>Name Line 2 <span style={{ fontWeight: 400, fontSize: 10, color: "var(--t2t-text-muted)" }}>(optional, max 35 chars)</span></label>
            <input className="t2t-fc" value={data.vendor_name_2 || ""} onChange={(e) => set("vendor_name_2", e.target.value.slice(0, 35))} placeholder="Additional name line for SAP" disabled={readOnly || isCheckerOrApprover} maxLength={35} />
          </div>

          <div className="t2t-fg">
            <label>Vendor Category *</label>
            <select className="t2t-fc" value={data.vendor_category || ""} onChange={(e) => set("vendor_category", e.target.value)} disabled={readOnly || isCheckerOrApprover}>
              <option value="">Select category...</option>
              {VENDOR_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {fieldErr("vendor_category")}
          </div>

          <div className="t2t-fg">
            <label>MSME Registered?</label>
            <div style={{ display: "flex", gap: 12 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, cursor: "pointer" }}>
                <input type="radio" checked={data.msme_registered === true} onChange={() => set("msme_registered", true)} disabled={readOnly || isCheckerOrApprover} /> Yes
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, cursor: "pointer" }}>
                <input type="radio" checked={data.msme_registered !== true} onChange={() => set("msme_registered", false)} disabled={readOnly || isCheckerOrApprover} /> No
              </label>
            </div>
          </div>

          {data.msme_registered && (
            <div className="t2t-fg">
              <label>MSME Category</label>
              <select className="t2t-fc" value={data.msme_category || ""} onChange={(e) => set("msme_category", e.target.value)} disabled={readOnly || isCheckerOrApprover}>
                <option value="">Select...</option>
                <option value="Micro">Micro</option>
                <option value="Small">Small</option>
                <option value="Medium">Medium</option>
              </select>
            </div>
          )}

          <div className="t2t-fg">
            <label>Purchasing Organisation *</label>
            <select className="t2t-fc" value={data.purchase_org || ""} onChange={(e) => set("purchase_org", e.target.value)} disabled={readOnly || isCheckerOrApprover}>
              <option value="">Select org unit...</option>
              {orgUnits.filter((o) => o.type === "Company" || o.type === "Group").map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
            {fieldErr("purchase_org")}
          </div>

          <div className="t2t-fg">
            <label>Company Code *</label>
            <select className="t2t-fc" value={data.company_code || ""} onChange={(e) => set("company_code", e.target.value)} disabled={readOnly || isCheckerOrApprover}>
              <option value="">Select company code...</option>
              {companyCodes.map((c) => (
                <option key={c.code} value={c.code}>{c.code} — {c.label}</option>
              ))}
            </select>
            {fieldErr("company_code")}
          </div>

          {plantCodes.length > 0 && (
            <div className="t2t-fg">
              <label>Plant Code</label>
              <select className="t2t-fc" value={data.plant_code || ""} onChange={(e) => set("plant_code", e.target.value)} disabled={readOnly || isCheckerOrApprover}>
                <option value="">Select plant...</option>
                {plantCodes.map((p) => (
                  <option key={p.code} value={p.code}>{p.code} — {p.label}</option>
                ))}
              </select>
            </div>
          )}

          {isFinanceStage && (
            <div className="t2t-fg">
              <label>Reconciliation Account (GL)</label>
              <input className="t2t-fc" value={data.recon_account || ""} onChange={(e) => set("recon_account", e.target.value)} placeholder="SAP GL account" disabled={readOnly} />
            </div>
          )}
        </div>
      )}

      {/* ======== SECTION 2: Address & Contact ======== */}
      {activeSection === 2 && (
        <div className="t2t-vendor-section">
          <div className="t2t-fg">
            <label>Street / Door No. *</label>
            <input className="t2t-fc" value={data.street_address || ""} onChange={(e) => set("street_address", e.target.value)} disabled={readOnly || isCheckerOrApprover} />
            {fieldErr("street_address")}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="t2t-fg">
              <label>City *</label>
              <input className="t2t-fc" value={data.city || ""} onChange={(e) => set("city", e.target.value)} disabled={readOnly || isCheckerOrApprover} />
              {fieldErr("city")}
            </div>
            <div className="t2t-fg">
              <label>State *</label>
              <select className="t2t-fc" value={data.state_code || ""} onChange={(e) => set("state_code", e.target.value)} disabled={readOnly || isCheckerOrApprover}>
                <option value="">Select state...</option>
                {INDIAN_STATES.map((s) => <option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}
              </select>
              {fieldErr("state_code")}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="t2t-fg">
              <label>PIN Code *</label>
              <input className="t2t-fc" value={data.postal_code || ""} onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 6); set("postal_code", v); }}
                placeholder="6 digits" disabled={readOnly || isCheckerOrApprover} maxLength={6}
                style={{ borderColor: data.postal_code && !PIN_REGEX.test(data.postal_code) ? "#DC2626" : undefined }} />
              {fieldErr("postal_code")}
            </div>
            <div className="t2t-fg">
              <label>Country</label>
              <input className="t2t-fc" value="India (IN)" disabled />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="t2t-fg">
              <label>Phone Number *</label>
              <input className="t2t-fc" value={data.phone_number || ""} onChange={(e) => set("phone_number", e.target.value.replace(/\D/g, "").slice(0, 15))} placeholder="10-15 digits" disabled={readOnly || isCheckerOrApprover} />
              {fieldErr("phone_number")}
            </div>
            <div className="t2t-fg">
              <label>Mobile / WhatsApp</label>
              <input className="t2t-fc" value={data.mobile_number || ""} onChange={(e) => set("mobile_number", e.target.value.replace(/\D/g, "").slice(0, 15))} placeholder="For WATI notifications" disabled={readOnly || isCheckerOrApprover} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="t2t-fg">
              <label>Email Address</label>
              <input className="t2t-fc" type="email" value={data.email || ""} onChange={(e) => set("email", e.target.value)} disabled={readOnly || isCheckerOrApprover} />
            </div>
            <div className="t2t-fg">
              <label>Website</label>
              <input className="t2t-fc" value={data.website || ""} onChange={(e) => set("website", e.target.value)} placeholder="https://..." disabled={readOnly || isCheckerOrApprover} />
            </div>
          </div>
        </div>
      )}

      {/* ======== SECTION 3: Tax & Compliance (Finance Stage) ======== */}
      {activeSection === 3 && (
        <div className="t2t-vendor-section">
          {!isFinanceStage && !isCheckerOrApprover && (
            <div style={{ padding: 12, background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 6, fontSize: 12, color: "#9A3412", marginBottom: 16 }}>
              Tax &amp; Compliance fields are filled by the Finance Data Controller at the Finance stage.
            </div>
          )}

          <div className="t2t-fg">
            <label>PAN Number * <span style={{ fontWeight: 400, fontSize: 10, color: "var(--t2t-text-muted)" }}>(10 chars: CCCCC####C)</span></label>
            <input className="t2t-fc" value={data.pan_number || ""} onChange={(e) => set("pan_number", e.target.value.toUpperCase().slice(0, 10))}
              placeholder="e.g. ABCDE1234F" disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)} maxLength={10}
              style={{ borderColor: data.pan_number && !PAN_REGEX.test(data.pan_number) ? "#DC2626" : data.pan_number && PAN_REGEX.test(data.pan_number) ? "#16A34A" : undefined }} />
            {data.pan_number && PAN_REGEX.test(data.pan_number) && (
              <div style={{ fontSize: 10, color: "#16A34A", marginTop: 2 }}>Valid PAN format. Type: {data.pan_type || "..."}</div>
            )}
            {data.pan_number && data.pan_number.length === 10 && !PAN_REGEX.test(data.pan_number) && (
              <div style={{ fontSize: 10, color: "#DC2626", marginTop: 2 }}>Invalid PAN format. Expected: CCCCC####C</div>
            )}
            {fieldErr("pan_number")}
          </div>

          {!isFastTrack && (
            <>
              <div className="t2t-fg">
                <label>GSTIN (Primary) * <span style={{ fontWeight: 400, fontSize: 10, color: "var(--t2t-text-muted)" }}>(15 chars)</span></label>
                <input className="t2t-fc" value={data.gstin || ""} onChange={(e) => set("gstin", e.target.value.toUpperCase().slice(0, 15))}
                  placeholder="e.g. 27ABCDE1234F1ZQ" disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)} maxLength={15}
                  style={{ borderColor: data.gstin && !GSTIN_REGEX.test(data.gstin) ? "#DC2626" : data.gstin && GSTIN_REGEX.test(data.gstin) ? "#16A34A" : undefined }} />
                {fieldErr("gstin")}
              </div>

              <div className="t2t-fg">
                <label>GST Registration Type *</label>
                <select className="t2t-fc" value={data.gst_registration_type || ""} onChange={(e) => set("gst_registration_type", e.target.value)} disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)}>
                  <option value="">Select...</option>
                  {GST_REG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="t2t-fg">
              <label>TDS Section Code</label>
              <input className="t2t-fc" value={data.section_code || "194Q"} disabled style={{ background: "#F8FAFC" }} />
              <div style={{ fontSize: 10, color: "var(--t2t-text-muted)", marginTop: 2 }}>Auto-filled: 194Q for all Indian vendors</div>
            </div>
            <div className="t2t-fg">
              <label>Withholding Tax Type</label>
              <input className="t2t-fc" value={data.withholding_tax_type || (data.pan_number ? "4Q" : "5Q")} disabled style={{ background: "#F8FAFC" }} />
              <div style={{ fontSize: 10, color: "var(--t2t-text-muted)", marginTop: 2 }}>{data.pan_number ? "4Q (PAN provided)" : "5Q (no PAN)"}</div>
            </div>
          </div>

          <div className="t2t-fg">
            <label>Payment Terms *</label>
            <select className="t2t-fc" value={data.payment_terms || ""} onChange={(e) => set("payment_terms", e.target.value)} disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)}>
              <option value="">Select payment terms...</option>
              {paymentTerms.map((t) => <option key={t.code} value={t.code}>{t.code} — {t.label}</option>)}
            </select>
            {fieldErr("payment_terms")}
          </div>

          <div className="t2t-fg">
            <label>Tax Exemption Reason</label>
            <textarea className="t2t-fc" rows={2} value={data.exemption_reason || ""} onChange={(e) => set("exemption_reason", e.target.value)} placeholder="If vendor claims tax exemption" disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)} />
          </div>
        </div>
      )}

      {/* ======== SECTION 4: Banking Details (Finance Stage) ======== */}
      {activeSection === 4 && (
        <div className="t2t-vendor-section">
          {!isFinanceStage && !isCheckerOrApprover && (
            <div style={{ padding: 12, background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 6, fontSize: 12, color: "#9A3412", marginBottom: 16 }}>
              Banking details are entered by the Finance Data Controller at the Finance stage.
            </div>
          )}

          <div className="t2t-fg">
            <label>Bank Name *</label>
            <input className="t2t-fc" value={data.bank_name || ""} onChange={(e) => set("bank_name", e.target.value)} disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)} />
            {fieldErr("bank_name")}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="t2t-fg">
              <label>Bank Account Number *</label>
              <input className="t2t-fc" value={data.bank_account_number || ""} onChange={(e) => set("bank_account_number", e.target.value.replace(/\D/g, ""))} disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)} />
              {fieldErr("bank_account_number")}
            </div>
            <div className="t2t-fg">
              <label>IFSC Code * <span style={{ fontWeight: 400, fontSize: 10, color: "var(--t2t-text-muted)" }}>(11 chars)</span></label>
              <input className="t2t-fc" value={data.ifsc_code || ""} onChange={(e) => set("ifsc_code", e.target.value.toUpperCase().slice(0, 11))}
                placeholder="e.g. SBIN0001234" disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)} maxLength={11}
                style={{ borderColor: data.ifsc_code && !IFSC_REGEX.test(data.ifsc_code) ? "#DC2626" : data.ifsc_code && IFSC_REGEX.test(data.ifsc_code) ? "#16A34A" : undefined }} />
              {fieldErr("ifsc_code")}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="t2t-fg">
              <label>Account Type *</label>
              <select className="t2t-fc" value={data.bank_account_type || ""} onChange={(e) => set("bank_account_type", e.target.value)} disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)}>
                <option value="">Select...</option>
                {BANK_ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              {fieldErr("bank_account_type")}
            </div>
            <div className="t2t-fg">
              <label>Account Holder Name *</label>
              <input className="t2t-fc" value={data.bank_account_name || ""} onChange={(e) => set("bank_account_name", e.target.value)}
                placeholder="Must match vendor name" disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)} />
              {fieldErr("bank_account_name")}
            </div>
          </div>

          <div className="t2t-fg">
            <label>UPI VPA (optional)</label>
            <input className="t2t-fc" value={data.upi_vpa || ""} onChange={(e) => set("upi_vpa", e.target.value)} placeholder="e.g. vendor@upi" disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)} />
          </div>
        </div>
      )}

      {/* ======== SECTION 5: Partner Functions ======== */}
      {activeSection === 5 && (
        <div className="t2t-vendor-section">
          <p style={{ fontSize: 12, color: "var(--t2t-text-muted)", marginBottom: 16 }}>
            Partner functions define the business roles this vendor plays in SAP. Select which apply.
          </p>
          {PARTNER_FUNCTIONS.map((pf) => (
            <div key={pf.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: "1px solid #F1F5F9" }}>
              <input type="checkbox" checked={pf.required ? true : (data[pf.id] || false)}
                onChange={(e) => !pf.required && set(pf.id, e.target.checked)}
                disabled={readOnly || isCheckerOrApprover || pf.required}
                style={{ marginTop: 2, width: 16, height: 16 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{pf.label}</div>
                <div style={{ fontSize: 11, color: "var(--t2t-text-muted)" }}>{pf.desc}</div>
              </div>
            </div>
          ))}

          {data.partner_function_oa && (
            <div className="t2t-fg" style={{ marginTop: 12 }}>
              <label>OA Vendor Reference</label>
              <input className="t2t-fc" value={data.ordering_address_vendor || ""} onChange={(e) => set("ordering_address_vendor", e.target.value)} placeholder="OSIF vendor number" disabled={readOnly || isCheckerOrApprover} />
            </div>
          )}
          {data.partner_function_pi && (
            <div className="t2t-fg" style={{ marginTop: 12 }}>
              <label>PI Vendor Reference</label>
              <input className="t2t-fc" value={data.invoicing_party_vendor || ""} onChange={(e) => set("invoicing_party_vendor", e.target.value)} placeholder="OSIF vendor number" disabled={readOnly || isCheckerOrApprover} />
            </div>
          )}
        </div>
      )}

      {/* ======== SECTION 6: Supporting Documents ======== */}
      {activeSection === 6 && (
        <div className="t2t-vendor-section">
          <p style={{ fontSize: 12, color: "var(--t2t-text-muted)", marginBottom: 16 }}>
            Upload required supporting documents. Accepted formats: PDF, JPG, PNG. Maximum 5MB per file unless noted.
          </p>
          {DOCUMENT_TYPES.map((doc) => {
            if (doc.condition === "msme_registered" && !data.msme_registered) return null;
            const isRequired = doc.requiredFor.includes(ag);
            return (
              <div key={doc.id} style={{ padding: "12px 0", borderBottom: "1px solid #F1F5F9" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <label style={{ fontSize: 13, fontWeight: 600 }}>
                    {doc.label} {isRequired && <span style={{ color: "#DC2626" }}>*</span>}
                  </label>
                  {data[doc.id] && <span style={{ fontSize: 10, color: "#16A34A", fontWeight: 600 }}>Uploaded</span>}
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input type="file" accept={doc.accept}
                    onChange={(e) => set(doc.id, e.target.files[0] || null)}
                    disabled={readOnly || isCheckerOrApprover}
                    style={{ fontSize: 12 }} />
                  {doc.hasExpiry && (
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <label style={{ fontSize: 11, color: "var(--t2t-text-muted)", whiteSpace: "nowrap" }}>Expiry:</label>
                      <input type="date" className="t2t-fc" style={{ fontSize: 11, width: 140, padding: "3px 6px" }}
                        value={data[`${doc.id}_expiry`] || ""} onChange={(e) => set(`${doc.id}_expiry`, e.target.value)}
                        disabled={readOnly || isCheckerOrApprover} />
                    </div>
                  )}
                </div>
                {fieldErr(doc.id)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

T2TVendorForm.propTypes = {
  data: PropTypes.object,
  onChange: PropTypes.func,
  stage: PropTypes.oneOf(["initiator", "checker", "finance", "approver"]),
  accountGroup: PropTypes.string,
  errors: PropTypes.object,
  readOnly: PropTypes.bool,
};

export default T2TVendorForm;
